import React from 'react';
import { format, parseISO } from 'date-fns';
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLifecycle } from '@/hooks/ml';
import { useDrillDown } from './DrillDownContext';

interface Cohort {
  month_joined: string;
  new_members: number;
  engaged_within_30d: number;
  engaged_within_90d: number;
  recently_active: number;
  likely_inactive: number;
}

export const MemberLifecycleCard: React.FC = () => {
  const { drillCboId } = useDrillDown();
  const { data, isLoading } = useLifecycle(drillCboId);
  const rows = (data?.monthly_cohorts ?? []) as unknown as Cohort[];

  const series = rows
    .map((r) => ({
      month: format(parseISO(r.month_joined), 'MMM yy'),
      new_members: r.new_members,
      engaged_within_30d: r.engaged_within_30d,
      engaged_within_90d: r.engaged_within_90d,
      recently_active: r.recently_active,
      likely_inactive: r.likely_inactive,
    }))
    .reverse(); // chronological

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Member life-cycle by join month</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-64 flex items-center justify-center text-sm text-gray-500">Loading…</div>
        ) : !series.length ? (
          <div className="h-64 flex items-center justify-center text-sm text-gray-500">No life-cycle data.</div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" tick={{ fontSize: 11 }} />
                <YAxis stroke="#6b7280" tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area
                  type="monotone"
                  dataKey="recently_active"
                  stackId="1"
                  stroke="#10b981"
                  fill="#a7f3d0"
                  name="Recently active"
                />
                <Area
                  type="monotone"
                  dataKey="engaged_within_90d"
                  stackId="1"
                  stroke="#3b82f6"
                  fill="#bfdbfe"
                  name="Engaged ≤90d"
                />
                <Area
                  type="monotone"
                  dataKey="likely_inactive"
                  stackId="1"
                  stroke="#f59e0b"
                  fill="#fde68a"
                  name="Likely inactive"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
